import React, { useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';

import { FlagsProvider } from '@atlaskit/flag';

import { AsyncIntlProvider } from '../common/utils/locale';
import { useMessagesForLocation } from '../common/utils/use-messages-for-location';
import { useNavBoundaryContext } from '../controllers/nav-boundary-context';
import { useMessageFlags } from '../controllers/use-message-flags';

import { ErrorBoundaryUI } from './error-boundary-ui';
import type { NavErrorBoundaryProps } from './types';

export function NavErrorBoundary(props: NavErrorBoundaryProps) {
	return (
		<AsyncIntlProvider>
			<FlagsProvider>
				<InnerErrorBoundary {...props} />
			</FlagsProvider>
		</AsyncIntlProvider>
	);
}

function InnerErrorBoundary({
	disableAnalytics = false,
	disableSentry = false,
	errorActionUI,
	errorFallbackUI,
	interactionId,
	location = 'unknown',
	onError,
	showFlags,
	silent,
	children,
	sentryConfig,
}: NavErrorBoundaryProps) {
	const { formatMessage } = useIntl();
	const { header, description, values } = useMessagesForLocation(location);
	const flags = useMessageFlags({ showFlags });

	const onErrorUIHandler = useCallback(
		(error: Error) => {
			// Show failure flag if enabled
			flags?.showFailure?.({
				title: formatMessage(header, values),
				description: formatMessage(description, values),
			});

			onError?.(error);
		},
		[description, header, flags, formatMessage, onError, values],
	);

	const { handleError } = useNavBoundaryContext({
		disableAnalytics,
		disableSentry,
		interactionId,
		location,
		onError: onErrorUIHandler,
		sentryConfig,
	});

	// When `silent` is true or `flags` are enabled, we don't want to show any UI on error
	const errorComponent =
		silent || flags ? null : (
			<ErrorBoundaryUI
				disableAnalytics={disableAnalytics}
				errorActionUI={errorActionUI}
				errorFallbackUI={errorFallbackUI}
				location={location}
			/>
		);

	return (
		<ErrorBoundary onError={handleError} fallback={errorComponent}>
			{children}
		</ErrorBoundary>
	);
}
